// api/requirementApi.js

import API from "./axiosConfig"; // Use configured Axios instance

// Base URL for the requirements API
const API_URL = "https://ims.holdthechild.net/api/requirements";

/**
 * Fetches all requirements for a specific project.
 * @param {string} projectId The ID of the project.
 * @returns {Promise<Array>} An array of requirement objects with fields: name, type, quantity, unit, unitCost, totalCost, budgetCode, status.
 */
export const getRequirementsByProjectId = async (projectId) => {
  try {
    const response = await API.get(`${API_URL}/${projectId}`);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      console.warn("No requirements found for project:", projectId);
      return [];
    }
    console.error("Error fetching requirements:", error);
    throw error;
  }
};

/**
 * Creates a new project requirement.
 * @param {object} requirementData The data for the new requirement.
 * @param {string} requirementData.name
 * @param {string} requirementData.type
 * @param {number} requirementData.quantity
 * @param {string} requirementData.unit
 * @param {number} requirementData.unitCost
 * @param {string} requirementData.budgetCode
 * @param {string} requirementData.projectId
 * @param {string} [requirementData.status] - Defaults to 'Pending' if not provided
 * @returns {Promise<object>} The newly created requirement object.
 */
export const createRequirement = async (requirementData) => {
  try {
    const response = await API.post(API_URL, requirementData);
    return response.data;
  } catch (error) {
    console.error("Error creating requirement:", error);
    throw error;
  }
};

/**
 * Updates an existing requirement by its ID.
 * @param {string} requirementId The ID of the requirement to update.
 * @param {object} updateData The updated data for the requirement, e.g., status.
 * @returns {Promise<object>} The updated requirement object.
 */
export const updateRequirement = async (requirementId, updateData) => {
  try {
    const response = await API.put(`${API_URL}/${requirementId}`, updateData);
    return response.data;
  } catch (error) {
    console.error("Error updating requirement:", error);
    throw error;
  }
};

/**
 * Deletes a requirement by its ID.
 * @param {string} requirementId The ID of the requirement to delete.
 * @returns {Promise<void>}
 */
export const deleteRequirement = async (requirementId) => {
  try {
    await API.delete(`${API_URL}/${requirementId}`);
  } catch (error) {
    console.error("Error deleting requirement:", error);
    throw error;
  }
};



/**
 * Fetches the budget status (Income vs Expenses) for a specific project.
 * @param {string} projectId The ID of the project.
 * @returns {Promise<object>} Object containing totalBudget, totalSpent, remainingBudget.
 */
export const getProjectBudgetStatus = async (projectId) => {
  try {
    const response = await API.get(`/projects/${projectId}/budget-status`);
    // ✏️ UPDATED: Handle the simplified response (which now returns projectTitle, targetBudget, totalFundsReceived)
    return response.data.data; 
  } catch (error) {
    console.error("Error fetching budget status:", error);
    // Return defaults if error occurs so the UI doesn't crash
    return {
        projectTitle: "Unknown",
        targetBudget: 0,
        totalFundsReceived: 0,
        budgetCode: "",
        currency: "USD"
    };
  }
};

/**
 * Bulk updates the status of multiple requirements.
 * @param {string[]} requirementIds Array of requirement IDs to update.
 * @param {string} status New status (Pending, Issued, Rejected, Cancelled, Requisitioned).
 * @param {string} projectId The ID of the project (for validation).
 * @returns {Promise<object>} Response with message and modifiedCount.
 */
export const bulkUpdateRequirementStatus = async (requirementIds, status, projectId) => {
  try {
    const response = await API.patch(`${API_URL}/bulk-update-status`, {
      requirementIds,
      status,
      projectId,
    });
    return response.data;
  } catch (error) {
    console.error("Error in bulk update of requirement statuses:", error);
    throw error;
  }
};